/* eslint-disable security/detect-possible-timing-attacks */
/* eslint-disable security/detect-object-injection */
/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */
enum FormStatus {
    SUCCESS,
    FAILED
}

import * as React from 'react';
import { IReturnsViewProps } from './returns';
import * as Msdyn from '@msdyn365-commerce/core';
import { verifyReCaptchaAsync } from '../../actions/extension/DataActionExtension.g';

export default (props: IReturnsViewProps) => {
    const {
        config,
        hideReCaptcha,
        context: { app }
    } = props;
    const recaptchaRef = React.useRef<any>();

    const [reCaptchaToken, setReCaptchaToken] = React.useState('');
    const [reCaptchaTokenError, setReCaptchaTokenError] = React.useState({
        error: false,
        message: ''
    });
    if (!hideReCaptcha) {
        const captchaSiteKey = props.context.app.config.reCaptchaSiteKey;
        const [isRECaptchaLoaded, setIsRECaptchaLoaded] = React.useState(false);

        const onReCaptchaLoad = () => {
            setIsRECaptchaLoaded(true);
        };

        if (Msdyn.msdyn365Commerce.isBrowser) {
            React.useEffect(() => {
                // Assign the component function to the window callback
                // @ts-ignore
                window.onReCaptchaLoad = onReCaptchaLoad;

                // @ts-ignore
                if (!window.grecaptcha) {
                    // Load the script only if it's not already available
                    const script = document.createElement('script');
                    script.src = 'https://www.google.com/recaptcha/api.js?onload=onReCaptchaLoad&render=explicit';
                    script.async = true;
                    script.defer = true;
                    document.head.append(script);
                    // @ts-ignore
                } else if (window.grecaptcha) {
                    // @ts-ignore
                    window.grecaptcha.reset();
                    // If reCAPTCHA is already loaded, call the function directly
                    onReCaptchaLoad();
                }

                // Clean up the global callback on component unmount
                return () => {
                    // @ts-ignore
                    window.onReCaptchaLoad = null;
                };
            }, []);
        }

        React.useEffect(() => {
            if (isRECaptchaLoaded) {
                // @ts-ignore
                window.grecaptcha.render(recaptchaRef.current, {
                    sitekey: captchaSiteKey,
                    callback: (token: string) => {
                        setReCaptchaToken(token);
                        if (token !== '') {
                            setReCaptchaTokenError({
                                error: false,
                                message: ''
                            });
                        }
                    }
                });
            }
        }, [isRECaptchaLoaded]);
    }

    const [formOperation, setFormOperation] = React.useState(FormStatus.SUCCESS);
    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!hideReCaptcha) {
            if (reCaptchaToken === '') {
                setReCaptchaTokenError({
                    error: true,
                    message: '⚠️ Please complete the reCAPTCHA before submitting.'
                });
                return;
            } else {
                setReCaptchaTokenError({
                    error: false,
                    message: ''
                });
                // Send token for server side verification
                const data = await verifyReCaptchaAsync({ callerContext: props.context.actionContext }, `${reCaptchaToken}`)
                    .then(res => {
                        if (res.isHuman) {
                            setReCaptchaTokenError({
                                error: false,
                                message: ''
                            });
                            if (Msdyn.msdyn365Commerce.isBrowser) {
                                const form = document.getElementById('returnForm') as HTMLFormElement;
                                const formData = new FormData(form);
                                // convert to json
                                const object: any = {};
                                formData.forEach((value, key) => {
                                    if (key === 'g-recaptcha-response') {
                                        //
                                    } else {
                                        object[key] = value;
                                    }
                                });

                                const url = props.context.app.config.returnEmailUrl;
                                try {
                                    fetch(url, {
                                        body: JSON.stringify(object),
                                        method: 'POST',
                                        headers: {
                                            'Content-Type': 'application/json'
                                        }
                                    })
                                        .then(res => {
                                            if (res.status === 200) {
                                                setOpen(true);
                                                setFormOperation(FormStatus.SUCCESS);
                                            } else {
                                                setFormOperation(FormStatus.FAILED);
                                            }
                                        })
                                        .catch(err => {
                                            setFormOperation(FormStatus.FAILED);
                                        });
                                } catch (e) {
                                    setFormOperation(FormStatus.FAILED);
                                }
                            }
                        } else {
                            setReCaptchaTokenError({
                                error: true,
                                message: '❌ reCAPTCHA verification failed. Please try again.'
                            });
                            return;
                        }
                    })
                    .catch(error => {
                        setReCaptchaTokenError({
                            error: true,
                            message: '❌ reCAPTCHA verification failed. Please try again.'
                        });
                        return;
                    });
                console.log(data);
            }
        } else {
            if (Msdyn.msdyn365Commerce.isBrowser) {
                const form = document.getElementById('returnForm') as HTMLFormElement;
                const formData = new FormData(form);
                // convert to json
                const object: any = {};
                formData.forEach((value, key) => {
                    if (key === 'g-recaptcha-response') {
                        //
                    } else {
                        object[key] = value;
                    }
                });

                const url = props.context.app.config.returnEmailUrl;
                try {
                    fetch(url, {
                        body: JSON.stringify(object),
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    })
                        .then(res => {
                            if (res.status === 200) {
                                setOpen(true);
                                setFormOperation(FormStatus.SUCCESS);
                            } else {
                                setFormOperation(FormStatus.FAILED);
                            }
                        })
                        .catch(err => {
                            setFormOperation(FormStatus.FAILED);
                        });
                } catch (e) {
                    setFormOperation(FormStatus.FAILED);
                }
            }
        }
    };
    const [open, setOpen] = React.useState(false);
    const closeModal = () => {
        setOpen(false);
        if (Msdyn.msdyn365Commerce.isBrowser) {
            const form = document.getElementById('returnForm') as HTMLFormElement;
            form.reset();
        }
    };
    return (
        <div className='ml-returns-container'>
            <div className='ml-returns-container-instructions'>
                <h2>RETURNS</h2>
                <p>Where returns are required, Morland aims to make this as simple as possible.</p>
                <p>Please enter the details of your order below and press submit to start your Returns Request.</p>
                <p>Your order ID will look similar to the highlighted number below and is available from our update emails.</p>
            </div>
            <div className='ml-returns-container-order-number'>
                <p>
                    Your Order <span>{config.orderIDPattern}</span>
                </p>
            </div>
            <div className='ml-returns-container-cautions'>
                <p>ALL DELIVERIES MUST BE CHECKED FOR DAMAGES AND SIGNED FOR ON RECEIPT.</p>
                <p>IF DAMAGES ARE NOT NOTED AT THE TIME OF DELIVERY WE WILL BE UNABLE TO PROCESS YOUR CLAIM.</p>
                <span>Discrepancies must be reported to us within 3 business days of receipt.</span>
            </div>
            <div className='ml-returns-container-form'>
                <form onSubmit={e => handleSubmit(e)} id='returnForm'>
                    <div className='ml-returns-container-form__form-field'>
                        <label>Order ID</label>
                        <input type='text' required name='OrderId' />
                    </div>
                    <div className='ml-returns-container-form__form-field'>
                        <label>Billing Last Name</label>
                        <input type='text' required name='LastName' />
                    </div>
                    <div className='ml-returns-container-form__form-field'>
                        <label>Email</label>
                        <input type='text' required name='Email' />
                    </div>
                    {!hideReCaptcha && (
                        <>
                            <div className='ml-g-recaptcha' ref={recaptchaRef}></div>
                            {reCaptchaTokenError.error && <div className='ml-g-recaptcha-error'>{reCaptchaTokenError.message}</div>}
                        </>
                    )}

                    <div className='ml-returns-container-form__button'>
                        <button type='submit'>SUBMIT</button>
                    </div>
                </form>
            </div>
            <div className='ml-returns-container-delivery-estimate'>
                <p>Your request will generally be responded to within 24 working hours.</p>
                <p>
                    For further information on return policies please see our <a href={app.routes.faq}>FAQ&apos;s</a>
                </p>
            </div>
            <BuyBoxModal
                show={open}
                modalClose={closeModal}
                status={formOperation}
                successMessage={config.returnOrderSuccessMessage}
                failureMessage={config.returnOrderFailureMessage}
            />
        </div>
    );
};

type BuyBoxModalProps = {
    modalClose?: () => void;
    show?: boolean;
    successMessage?: string;
    failureMessage?: string;
    status: FormStatus;
};

const hiddenStyle: React.CSSProperties = {
    visibility: 'hidden',
    zIndex: 1000,
    display: 'block'
};

const visibleStyle: React.CSSProperties = {
    visibility: 'visible',
    zIndex: 1000,
    display: 'block'
};

export const BuyBoxModal = (props: BuyBoxModalProps) => {
    const { show, modalClose, status, failureMessage, successMessage } = props;

    return (
        <div tabIndex={-1} style={show ? visibleStyle : hiddenStyle}>
            <div>
                <div className={`msc-modal ${show ? 'show' : 'fade'}`} style={{ display: 'block' }}>
                    <div className='msc-modal__dialog msc-add-to-cart__dialog' role='dialog' aria-modal='true' tabIndex={0}>
                        <div className='msc-modal__content' role='document'>
                            <div className='msc-add-to-cart__dialog__header msc-modal__header'>
                                <h5 className='msc-modal__title'>
                                    <span></span>
                                    <span></span>
                                </h5>
                                <button type='button' onClick={modalClose} className='msc-modal__close-button' aria-label='Close' />
                            </div>
                            <div className='msc-add-to-cart__dialog__body msc-modal__body'>
                                <div className='msc-add-to-cart__dialog__product-price'>
                                    <span className='msc-price'>
                                        <span className='msc-price__actual' itemProp='price'>
                                            {status === FormStatus.SUCCESS ? successMessage : failureMessage}
                                        </span>
                                    </span>
                                </div>
                            </div>
                            <div className='msc-add-to-cart__dialog__footer msc-modal__footer'>
                                <button
                                    type='button'
                                    onClick={modalClose}
                                    aria-label='Close Modal'
                                    className='msc-add-to-cart__dialog__go-to-cart msc-btn msc-close-btn'
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={`msc-modal__backdrop ${show ? 'show' : 'fade'}`} onClick={modalClose} />
            </div>
        </div>
    );
};
